/**
 * Whether the current user may see the redesigned "Detailed" dashboard view.
 *
 * Generally available since 2026-09-27. It was allowlisted to one email while
 * in limited release; the Map view had already been opened to everyone.
 * @returns Always true.
 */
export default function canUseDeviceMonitorViews(): boolean {
  return true;
}

/**
 * Whether the current user may see the OPC-N3 bin analysis page.
 *
 * Generally available since 2026-09-24; the page shows an empty state for
 * devices without an OPC-N3 sensor, so there is nothing to gate.
 * @returns Always true.
 */
export function canUseOpcBinAnalysis(): boolean {
  return true;
}

/**
 * Whether the current user may see the APS channel analysis page.
 *
 * Generally available since 2026-09-24, alongside the OPC-N3 page.
 * @returns Always true.
 */
export function canUseApsChannelAnalysis(): boolean {
  return true;
}

/**
 * Emails allowed to reach the cross-tenant BLE ping admin pages.
 *
 * MUST stay in step with the server's `telosair.ble.admin-emails` property —
 * this check only decides whether the page renders or redirects; the API is the
 * real gate and returns 403 regardless. If the two disagree, the failure is
 * quiet in both directions: an allowed email gets a blank page, and a removed
 * one gets a page full of 403s instead of a redirect.
 *
 * Set REACT_APP_BLE_ADMIN_EMAILS (comma-separated) to match the server when it
 * is overridden; the default is the same single address the server defaults to,
 * so an environment that configures neither behaves as it always has.
 *
 * This replaces a `const ADMIN_EMAIL` copied into each BLE page. Five other
 * admin surfaces (side nav, overview, payments, AI sessions, the client
 * switcher) still carry their own copies — consolidating those is a wider auth
 * change than the BLE work, and is deliberately left alone here.
 */
const BLE_ADMIN_EMAILS: string[] = (
  process.env.REACT_APP_BLE_ADMIN_EMAILS || 'suresh@potsdamsensors.com'
)
  .split(',')
  .map((email) => email.trim().toLowerCase())
  .filter((email) => email.length > 0);

/**
 * Whether this user may view every user's BLE pings.
 * @param userEmail - The signed-in user's email.
 * @returns True when the email is on the BLE admin allow-list.
 */
export function canUseBleAdmin(userEmail: string | null | undefined): boolean {
  if (!userEmail) return false;
  return BLE_ADMIN_EMAILS.includes(userEmail.trim().toLowerCase());
}
